import { Component, OnInit, OnChanges, ViewChild, Input } from '@angular/core'
import { ProductMove } from 'depoto-core/src/entities'
// import Chart from 'chart.js/auto'
import { TranslateService } from '@ngx-translate/core'
import { CoreService } from '../../services'
import { FormatPricePipe } from '../../pipes'

type ChartGroups = {
  [dateLabel: string]: {
    checkouts: {
      [checkoutName: string]: {
        value: number
        barPercent: number
        stackedPercent: number
      }
    }
    sum: number
    stackedPercentSum: number
  }
}

type ChartDataGroup = {
  dateLabel: string
  dateSortable: string
  sum: number
  stackedPercentSum: number
  title: string
  checkouts: Array<{
    name: string
    color: string
    value: number
    suffix: string
    title: string
    barPercent: number
    stackedPercent: number
  }>
}

type CheckoutLegend = {
  name: string
  color: string
}

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  providers: [FormatPricePipe],
})
export class ChartComponent implements OnInit, OnChanges {
  @ViewChild('chartElement') chartElement: any
  @ViewChild('chartContentEl') chartContentEl: any
  @Input() productMoves: ProductMove[] = []
  @Input() type: 'separateCheckouts' | 'mergedCheckout' = 'separateCheckouts'
  @Input() chartTime: 'stats.chart.month' | 'stats.chart.day' = 'stats.chart.month'
  @Input() chartValue: 'stats.chart.value' | 'stats.chart.amount' = 'stats.chart.value'
  @Input() isPricesWithVat: boolean = true
  @Input() isLoading: boolean = false
  chartId: string
  devicePixelRatio: number = window.devicePixelRatio > 1.5 ? 1.5 : window.devicePixelRatio
  chartCanvasWidth: number = 450 / this.devicePixelRatio
  chartCanvasHeight: number = 450 / this.devicePixelRatio
  chartGroups: ChartGroups = {}
  chartData: ChartDataGroup[] = []
  chartBackgroundYLabels: string[] = []
  legendMaxValue: number = 0
  chartMaxValue: number = 0
  checkouts: { [name: string]: string } = {}
  checkoutLegend: CheckoutLegend[] = []
  isNoDataForChart = false
  defaultChartColors = [
    'rgba(237, 28, 36, 1)',
    'rgba(73, 190, 201,1)',
    'rgba(239, 161, 49,1)',
    'rgba(130, 220, 132, 1)',
    'rgba(54, 162, 235, 1)',
    'rgba(255, 206, 86, 1)',
    'rgba(24, 180, 60, 1)',
    'rgba(55, 250, 250, 1)',
    'rgba(230, 0, 250, 1)',
    'rgba(222, 70, 70, 1)',
    '#8E44AD',
    '#34495E',
    '#A3E048',
    '#FF6F61',
    '#8B572A',
    '#808000',
    '#1ABC9C',
    '#017373',
    '#DAA520',
    '#B39DDB',
  ]
  lastShownTooltipElement: any

  constructor(
    public core: CoreService,
    public fmtPrice: FormatPricePipe,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.chartId = `chart_${this.type}_${new Date().getTime().toFixed(0).substr(-5)}_${Math.floor(Math.random() * 20)}`
    this.chartCanvasWidth = 900 / this.devicePixelRatio
    this.chartCanvasHeight = 450 / this.devicePixelRatio
  }

  ngOnChanges(changes) {
    if (changes?.productMoves?.currentValue?.length > 0) {
      this.isNoDataForChart = false
      if (this.chartId && this.chartId.length > 0) {
        this.initChart()
      }
    } else if (changes?.productMoves?.currentValue?.length === 0) {
      if (!changes.productMoves.firstChange) {
        // this.loading = false
        this.isNoDataForChart = true
      }
    }
    if (
      (changes?.type || changes?.chartTime || changes?.chartValue) &&
      (changes?.productMoves?.currentValue?.length || this.productMoves?.length)
    ) {
      this.initChart()
    }
    // if (
    //   (changes?.type || changes?.chartTime || changes?.chartValue || changes.isPricesWithVat) &&
    //   (changes?.productMoves?.currentValue?.length || this.productMoves?.length)
    // ) {
    //   this.initChart()
    // }
  }

  initChart(): void {
    if (!this.productMoves) {
      console.warn('no data for chart init!')
      return
    }
    // this.loading = true
    this.initChartGroups()
    // this.loading = false
  }

  initChartGroups() {
    const hideTooltips = () => {
      try {
        const jQuery = typeof window !== 'undefined' ? (window as any).$ || (window as any).jQuery : null
        if (jQuery && typeof jQuery === 'function') {
          const el = jQuery('[data-toggle="tooltip"]')
          if (el && typeof el.tooltip === 'function') {
            el.tooltip('hide')
          }
        }
      } catch (e) {
        console.warn('Failed to hide tooltip:', e)
      }
    }
    document.body.removeEventListener('scroll', hideTooltips)
    const groups = this.getChartGroups()
    this.chartGroups = groups
    this.setUniqueCheckoutsAndColors()
    this.chartData = []
    for (const dateLabel in groups) {
      let checkouts = []
      Object.keys(groups[dateLabel].checkouts).forEach(checkoutName => {
        checkouts.push({
          name: checkoutName,
          color: this.checkouts[checkoutName],
          value: Number(groups[dateLabel].checkouts[checkoutName].value).toFixed(2),
          suffix: this.chartValue === 'stats.chart.value' ? this.core.services.user.user?.company?.currency?.id : 'Ks',
          title:
            this.chartValue === 'stats.chart.value'
              ? this.fmtPrice.transform(
                  Number(groups[dateLabel].checkouts[checkoutName].value),
                  this.core.services.user.user?.company?.currency,
                )
              : `${Number(groups[dateLabel].checkouts[checkoutName].value).toFixed(2)} Ks`,
          barPercent: groups[dateLabel].checkouts[checkoutName].barPercent,
          stackedPercent: groups[dateLabel].checkouts[checkoutName].stackedPercent,
        })
      })
      checkouts = [...checkouts].sort((a, b) => (a.name > b.name ? 1 : -1))
      this.chartData.push({
        dateLabel: this.getDateLabelFromDateStr(dateLabel, false),
        dateSortable: dateLabel,
        sum: groups[dateLabel].sum,
        stackedPercentSum: groups[dateLabel].stackedPercentSum,
        title:
          this.chartValue === 'stats.chart.value'
            ? this.fmtPrice.transform(Number(groups[dateLabel].sum), this.core.services.user.user?.company?.currency)
            : `${Number(groups[dateLabel].sum).toFixed(2)} Ks`,
        checkouts,
      })
    }
    this.chartData = [...this.chartData].sort((a, b) => (a.dateSortable > b.dateSortable ? 1 : -1))
    setTimeout(() => {
      if (this.chartContentEl?.nativeElement) {
        this.chartContentEl.nativeElement.scrollLeft = 99999999
      }
      try {
        const jQuery = typeof window !== 'undefined' ? (window as any).$ || (window as any).jQuery : null
        if (jQuery && typeof jQuery === 'function') {
          const el = jQuery('[data-toggle="tooltip"]')
          if (el && typeof el.tooltip === 'function') {
            el.tooltip({ trigger: 'click hover' })
          }
          if (el && typeof el.on === 'function') {
            el.on('show.bs.tooltip', () => {
              hideTooltips()
            })
          }
        }
      } catch (e) {
        console.warn('Failed to initialize chart tooltip:', e)
      }
      document.body.addEventListener('scroll', hideTooltips)
    }, 10)
  }

  getChartGroups(): ChartGroups {
    const groups = {}
    this.productMoves.forEach(pm => {
      const dateLabel = this.getDateLabelFromDateStr(pm.created, true)
      const checkoutLabel = this.getCheckoutLabelFromProductMove(pm)
      const value = this.getValueLabelFromProductMove(pm)
      if (groups[dateLabel]) {
        if (groups[dateLabel].checkouts[checkoutLabel]) {
          groups[dateLabel].checkouts[checkoutLabel].value = groups[dateLabel].checkouts[checkoutLabel].value + value
        } else {
          groups[dateLabel].checkouts[checkoutLabel] = {
            value,
            barPercent: 0,
            stackedPercent: 0,
          }
        }
        groups[dateLabel].sum = groups[dateLabel].sum + value
      } else {
        groups[dateLabel] = {
          checkouts: {
            [checkoutLabel]: {
              value,
              barPercent: 0,
              stackedPercent: 0,
            },
          },
          sum: value,
        }
      }
    })
    // set max bar height
    this.chartMaxValue = 0
    Object.keys(groups).forEach(dateLabel => {
      if (this.type === 'separateCheckouts') {
        Object.keys(groups[dateLabel].checkouts).forEach(checkoutLabel => {
          if (this.chartMaxValue < groups[dateLabel].checkouts[checkoutLabel].value) {
            this.chartMaxValue = groups[dateLabel].checkouts[checkoutLabel].value
          }
        })
      } else if (this.type === 'mergedCheckout') {
        if (this.chartMaxValue < groups[dateLabel].sum) {
          this.chartMaxValue = groups[dateLabel].sum
        }
      }
    })
    this.initChartBackground()
    // calc stackedPercent and barPercent for bar heights
    Object.keys(groups).forEach(dateLabel => {
      Object.keys(groups[dateLabel].checkouts).forEach(checkoutLabel => {
        groups[dateLabel].checkouts[checkoutLabel].stackedPercent = (
          (groups[dateLabel].checkouts[checkoutLabel].value / groups[dateLabel].sum) *
          100
        ).toFixed(2)
        groups[dateLabel].checkouts[checkoutLabel].barPercent = (
          (groups[dateLabel].checkouts[checkoutLabel].value / this.legendMaxValue) *
          100
        ).toFixed(2)
      })
      groups[dateLabel].stackedPercentSum = ((groups[dateLabel].sum / this.legendMaxValue) * 100).toFixed(2)
    })
    return groups
  }

  getDateLabelFromDateStr(date: string, isSortable: boolean): string {
    const d = new Date(date)
    if (this.chartTime === 'stats.chart.month') {
      if (isSortable) {
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2)
      } else {
        return `${d.getMonth() + 1}/${d.getFullYear()}`
      }
    } else if (this.chartTime === 'stats.chart.day') {
      if (isSortable) {
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
      } else {
        return `${d.getDate()}.${d.getMonth() + 1}`
      }
    }
  }

  getCheckoutLabelFromProductMove(pm: ProductMove): string {
    return pm.pack.checkout?.name || ''
  }

  getValueLabelFromProductMove(pm: ProductMove): number {
    if (this.chartValue === 'stats.chart.amount') {
      return pm.amount
    } else if (this.chartValue === 'stats.chart.value') {
      const priceProp = this.isPricesWithVat ? 'purchasePriceAllWithVat' : 'purchasePriceAll'
      return pm[priceProp]
    }
  }

  setUniqueCheckoutsAndColors() {
    const checkoutNames: string[] = []
    Object.keys(this.chartGroups).forEach(dateLabel => {
      Object.keys(this.chartGroups[dateLabel].checkouts).forEach(checkoutLabel => {
        checkoutNames.push(checkoutLabel)
      })
    })
    const uniqueNames = Array.from(new Set(checkoutNames)).sort((a, b) => (a > b ? 1 : -1))
    this.checkouts = {}
    this.checkoutLegend = []
    const colors = Array(uniqueNames.length).fill(this.defaultChartColors).flat()
    let i = 0
    for (const uniqueName of uniqueNames) {
      this.checkouts[uniqueName] = colors[i]
      this.checkoutLegend.push({
        name: uniqueName,
        color: colors[i],
      })
      i++
    }
  }

  initChartBackground() {
    const ceilTo =
      this.chartMaxValue < 10
        ? 1
        : this.chartMaxValue < 50
          ? 5
          : this.chartMaxValue < 100
            ? 10
            : this.chartMaxValue < 500
              ? 50
              : this.chartMaxValue < 1000
                ? 100
                : this.chartMaxValue < 10000
                  ? 200
                  : this.chartMaxValue < 50000
                    ? 500
                    : 1000
    // const roundTo =
    //   this.chartMaxValue < 10
    //     ? 1 : this.chartMaxValue < 50
    //       ? 5 : this.chartMaxValue < 100
    //         ? 10 : this.chartMaxValue < 500
    //           ? 50 : this.chartMaxValue < 1000
    //             ? 100 : this.chartMaxValue < 5000
    //               ? 500 : this.chartMaxValue < 10000
    //                 ? 1000 : this.chartMaxValue < 50000
    //                   ? 5000 : 10000
    this.legendMaxValue = this.roundTo(this.chartMaxValue + this.chartMaxValue / 6 + ceilTo, ceilTo)
    const roundTo =
      this.legendMaxValue / 10 < 10
        ? 1
        : this.legendMaxValue / 10 < 50
          ? 5
          : this.legendMaxValue / 10 < 100
            ? 10
            : this.legendMaxValue / 10 < 500
              ? 50
              : this.legendMaxValue / 10 < 10000
                ? 100
                : 500
    const yTick = this.roundTo(this.legendMaxValue / 10, roundTo)
    const yLabels = []
    for (let i = 1; i <= 10; i++) {
      yLabels.push(
        this.chartValue === 'stats.chart.value'
          ? this.fmtPrice.transform(yTick * i, this.core.services.user.user?.company?.currency, 0)
          : `${yTick * i} Ks`,
      )
      // yLabels.push(this.roundTo(this.chartMaxValue / 10 * i, roundTo))
    }
    this.chartBackgroundYLabels = yLabels.reverse()
  }

  formatCurrencyAmountForTooltip(amount: number | string, fractionDigits = 0): string {
    return Number(amount)
      .toFixed(fractionDigits)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  roundTo(value: number, roundTo: number = 1000) {
    return Math.ceil(value / roundTo) * roundTo
  }
}
